import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import "./OverflowText.scss";

type OverflowTextProps = {
  text: string;
  /** 1 = single line ellipsis, 2+ = multi-line clamp. */
  lines?: number;
  as?: "span" | "div" | "p" | "dd" | "label";
  className?: string;
  htmlFor?: string;
};

/** Truncates with ellipsis; native hover title only when the text is actually cut off. */
function OverflowText({ text, lines = 1, as = "span", className = "", htmlFor }: OverflowTextProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const truncated =
      lines > 1
        ? el.scrollHeight - el.clientHeight > 1
        : el.scrollWidth - el.clientWidth > 1;
    setIsTruncated(truncated);
  }, [lines]);

  useLayoutEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, text]);

  const Tag = as as any;
  const clampClass = lines > 1 ? "overflow-text overflow-text--clamp" : "overflow-text overflow-text--single";
  const style = lines > 1 ? ({ WebkitLineClamp: lines } as React.CSSProperties) : undefined;
  const showTitle = isTruncated && text && text !== "—";

  return (
    <Tag
      ref={ref}
      className={`${clampClass}${className ? ` ${className}` : ""}`}
      style={style}
      title={showTitle ? text : undefined}
      htmlFor={htmlFor}
    >
      {text}
    </Tag>
  );
}

export default OverflowText;
